import { FC, Ref, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetClientListWithCode } from "../../../../../api/services/client/clientApis";
import { useGetCountryListByPage } from "../../../../../api/services/common/commonApis";
import {
  useGetAllProgramTemplateForDropdown,
  useGetLineOfBusiness,
} from "../../../../../api/services/programTemplate/programTemplateApis";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
} from "../../../../../components";
import {
  LANGUAGE_CODE,
  PAGE_NAME_FOR_COUNTRY_DROPDOWN
} from "../../../../../helpers/constants";
import {
  getValueOrBlank,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import { BulkCreateProgramClientList } from "../../../../bulkCreateProgram/modelsAndSchema/Models";
import { BulkEditSearchParticipantFormKey } from "../../../modelsAndSchema/Model";
import { bulkEditProgramSearchParticipantKeys } from "../../../siteCore/siteCoreKeys";
import { ClientAutoComplete } from "../../../../../components/clientAutoComplete/ClientAutoComplete";

export interface SearchParticipantsFormProps {
  pageId: string;
  deliveryCountryRef: Ref<{ resetValues: () => void }>;
  productLineRef: Ref<{ resetValues: () => void }>;
}

export const SearchParticipantsForm: FC<SearchParticipantsFormProps> = ({
  pageId,
  deliveryCountryRef,
  productLineRef,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { control, getValues, setValue } = useFormContext();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const clientWatch = useWatch({
    name: BulkEditSearchParticipantFormKey.clientId,
    control,
  });

  useEffect(() => {
    const selectedClient = getValues(BulkEditSearchParticipantFormKey.clientId);
    if (selectedClient) {
      const client = clientList.find(
        (client: BulkCreateProgramClientList) => client.id === selectedClient,
      );
      setValue(
        BulkEditSearchParticipantFormKey.clientName,
        getValueOrBlank(client?.name),
      );
    }
  }, [clientWatch]);

  const { useFetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();

  const getLabel = useFetchLabel(
    bulkEditProgramSearchParticipantKeys.searchParticipantsKey,
  );

  const {
    data: clientList,
    isFetching: clientPending,
    isError: clientError,
  } = useGetClientListWithCode();

  const {
    data: countryListData,
    isError: isCountryError,
    isFetching: isCountryFetching,
  } = useGetCountryListByPage(PAGE_NAME_FOR_COUNTRY_DROPDOWN.BULK_PROCESS);

  const {
    data: lineOfBusinessData,
    isError: isLineOfBusinessError,
    isFetching: isBusinessFetching,
  } = useGetLineOfBusiness(LANGUAGE_CODE.EN_US);

  const {
    data: programTemplateList,
    isError: isProgramError,
    isFetching: isProgramFetching,
  } = useGetAllProgramTemplateForDropdown();

  const clientListOptions = useMemo(() => {
    if (isNotEmpty(clientList)) {
      return clientList.map((client: BulkCreateProgramClientList) => ({
        label: client.name,
        value: client.id,
        clientCode: client.clientCode,
      }));
    }
  }, [clientList]);

  useEffect(() => {
    if (
      clientError ||
      isCountryError ||
      isLineOfBusinessError ||
      isProgramError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
    dispatch(toggleLoader({ isVisible: false }));
  }, [clientError, isCountryError, isLineOfBusinessError, isProgramError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          clientPending ||
          isCountryFetching ||
          isBusinessFetching ||
          isProgramFetching,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [clientPending, isCountryFetching, isBusinessFetching, isProgramFetching]);

  return (
    <MuiBox>
      <MuiGrid
        container
        className="form-container"
        data-testid="search-participant-form"
      >
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            name={BulkEditSearchParticipantFormKey.clientId}
            control={control}
          >
            <ClientAutoComplete
              isMandatory
              label={getLabel(bulkEditProgramSearchParticipantKeys.client)}
              formId={BulkEditSearchParticipantFormKey.clientId}
              control={control}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={BulkEditSearchParticipantFormKey.deliveryCountry}
            ref={deliveryCountryRef}
          >
            <MuiMultiSelect
              label={getLabel(
                bulkEditProgramSearchParticipantKeys.deliveryCountry,
              )}
              options={setApiDataToDropDownOption(countryListData, true)}
              showSearch
              showChip
              id={`${pageId}-${BulkEditSearchParticipantFormKey.deliveryCountry}`}
              chipId={`${pageId}-${BulkEditSearchParticipantFormKey.deliveryCountry}-chip`}
              dialogTitle={getLabel(
                bulkEditProgramSearchParticipantKeys.deliveryCountry,
              )}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={BulkEditSearchParticipantFormKey.productLine}
            ref={productLineRef}
          >
            <MuiMultiSelect
              label={getLabel(bulkEditProgramSearchParticipantKeys.productLine)}
              options={setApiDataToDropDownOption(lineOfBusinessData)}
              showSearch
              showChip
              id={`${pageId}-${BulkEditSearchParticipantFormKey.productLine}`}
              chipId={`${pageId}-${BulkEditSearchParticipantFormKey.productLine}-chip`}
              dialogTitle={getLabel(
                bulkEditProgramSearchParticipantKeys.productLine,
              )}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={BulkEditSearchParticipantFormKey.programTemplate}
          >
            <MuiSelect
              label={getLabel(
                bulkEditProgramSearchParticipantKeys.programTemplate,
              )}
              options={setApiDataToDropDownOption(programTemplateList)}
              value=""
              visibleDefaultSelectOption
              id={`${pageId}-${BulkEditSearchParticipantFormKey.programTemplate}`}
              placeholder={commonLabels.selectPlaceHolder}
              showSearch
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={BulkEditSearchParticipantFormKey.participantStatus}
          >
            <MuiSelect
              label={getLabel(
                bulkEditProgramSearchParticipantKeys.participantStatus,
              )}
              options={fetchDropdownValues(
                bulkEditProgramSearchParticipantKeys.searchParticipantsKey,
                bulkEditProgramSearchParticipantKeys.participantStatusList,
              )}
              value=""
              visibleDefaultSelectOption
              id={`${pageId}-${BulkEditSearchParticipantFormKey.participantStatus}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
